/**
 * VoicePicker Component
 *
 * Gallery view of ElevenLabs preset voices with preview functionality.
 * Allows users to browse and select from curated professional voices.
 */

import { useState } from 'react';
import { useElevenLabsVoices, type PresetVoice, getVoiceDisplayName } from '@/lib/elevenlabs-voices';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Check, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/utils/supabase';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VoicePickerProps {
  userId: string;
  selectedVoiceId?: string;
  onSelect: (voice: PresetVoice) => void;
}

export function VoicePicker({ userId, selectedVoiceId, onSelect }: VoicePickerProps) {
  const { toast } = useToast();
  const { voices, loading: voicesLoading, error: voicesError } = useElevenLabsVoices();
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  /**
   * Generate and play real ElevenLabs voice preview
   * Uses actual ElevenLabs API for accurate voice representation
   */
  const playPreview = async (voice: PresetVoice) => {
    setGeneratingId(voice.id);
    setError(null);

    try {
      // Sample greeting for preview (short to minimize cost)
      const sampleText = 'Thank you for calling. Please hold while we connect you.';

      // Call ElevenLabs API via edge function
      const { data, error: apiError } = await supabase.functions.invoke('elevenlabs-generate-greeting', {
        body: {
          userId,
          voiceId: voice.elevenLabsId,
          greetingText: sampleText,
          isPreview: true
        }
      });

      if (apiError) {
        throw apiError;
      }

      if (!data?.success) {
        if (data?.rateLimitExceeded) {
          setError(data.error);
          toast({
            title: 'Rate Limit Exceeded',
            description: data.error,
            variant: 'destructive'
          });
          return;
        }
        throw new Error(data?.error || 'Failed to generate preview');
      }

      // Store preview URL for this voice
      setPreviewUrls((prev) => ({ ...prev, [voice.id]: data.audioUrl }));

      // Play the audio
      const audio = new Audio(data.audioUrl);
      setPlayingId(voice.id);

      audio.onended = () => setPlayingId(null);
      audio.onerror = () => {
        setPlayingId(null);
        toast({
          title: 'Playback Error',
          description: 'Failed to play audio. Please try again.',
          variant: 'destructive'
        });
      };

      await audio.play();

      toast({
        title: 'Preview Generated',
        description: `Cost: $${data.cost || '0.003'} (${data.characters || sampleText.length} characters)`
      });

    } catch (err: any) {
      console.error('Preview generation error:', err);
      const errorMessage = err.message || 'Failed to generate preview';
      setError(errorMessage);
      toast({
        title: 'Preview Failed',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Select a professional voice for your call greetings. Click "Preview" to hear the actual ElevenLabs AI voice.
      </p>

      {(error || voicesError) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">{error || voicesError}</AlertDescription>
        </Alert>
      )}

      {voicesLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading voices...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {voices.map((voice) => {
          const isSelected = selectedVoiceId === voice.elevenLabsId;
          const isGenerating = generatingId === voice.id;
          const isPlaying = playingId === voice.id;

          return (
            <Card
              key={voice.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'border-2 border-primary shadow-lg' : 'border'
              }`}
              onClick={() => onSelect(voice)}
            >
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold flex items-center gap-2">
                      {getVoiceDisplayName(voice)}
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      {voice.labels?.[0] || voice.category}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {voice.accent || voice.gender || 'Professional'}
                  </Badge>
                </div>

                <p className="text-sm mb-3">{voice.description}</p>

                <div className="flex gap-2 mb-3">
                  {(voice.labels || []).slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      playPreview(voice);
                    }}
                    disabled={isGenerating || isPlaying}
                    className="flex-1"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : isPlaying ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Playing...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-1" />
                        Preview
                      </>
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onSelect(voice)}
                    className="flex-1"
                  >
                    {isSelected ? (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Selected
                      </>
                    ) : (
                      'Select'
                    )}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground mt-3 italic">
                  <strong>Best for:</strong> {voice.useCase || 'Professional communications'}
                </p>
              </CardContent>
            </Card>
          );
        })}
        </div>
      )}
    </div>
  );
}
